import React, {useCallback, useState} from "react";
import {operationWebSocket} from "./WebSocketProcess";
import {Button, ButtonProps, Tooltip} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import {ProgressDialog} from "./ProgressDialog";
import {Lifetime} from "../../util/Lifetime";
import {AsyncChannel} from "../../util/concurrency/AsyncChannel";
import {ProgressMessage} from "../../api/Model";
import {apiErrorMessage} from "../../util/ApiErrorMessage";
import {collect} from "../../util/concurrency/AsyncGeneratorUtil";
import {useAuthStore} from "../../app-layout/authStore";
import {ControlWithProgress} from "./ControlWithProgress";

export function WebSocketOperationButton({endpoint, actionButtonLabel, description, icon, onFinish, ...props}: {
    endpoint: string,
    actionButtonLabel: string,
    description: string,
    icon?: React.ReactNode,
    onFinish?: () => void | Promise<void>,
} & ButtonProps) {
    const [process, setProcess] = useState<{ lifetime: Lifetime, progress: AsyncGenerator<ProgressMessage, void> }>();
    const token = useAuthStore(state => state.token);

    const runOperation = useCallback(async () => {
        if (!token) return;

        const wsLifetime = new Lifetime();
        const channel = new AsyncChannel<ProgressMessage>(wsLifetime);
        setProcess({
            lifetime: wsLifetime,
            progress: channel.incoming(),
        });

        try {
            await collect(
                operationWebSocket(wsLifetime, token, endpoint),
                update => channel.send(update)
            );
            await onFinish?.()
        } catch (e) {
            channel.send({type: 'error', message: apiErrorMessage(e) || 'Operation failed'});
            console.error(e);
        } finally {
            wsLifetime.terminate();
            setProcess(undefined);
        }
    }, [endpoint, token, onFinish, setProcess]);

    return (
        <Tooltip title={description}>
            <ControlWithProgress loading={process !== undefined}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={icon || <SyncIcon/>}
                    onClick={runOperation}
                    disabled={process !== undefined}
                    {...props}
                >
                    {actionButtonLabel}
                </Button>
                <ProgressDialog initialMessage={description} process={process}/>
            </ControlWithProgress>
        </Tooltip>
    );
}
